import React, { useReducer } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import Api from './Api';
import ExternalViewHeader from './ExternalViewHeader';
import { sharedClasses } from '@/components/NewUI/Components/CustomUIElements/sharedClasses';
import { styles } from './styles';
import dompurify from 'dompurify';
import Attachments from './Attachments';
import { ExternalViewReducer, InitialExternalViewState } from './reducer';
import StyledSnackbar, {
  ISnackbarInput
} from '@/components/NewUI/Components/CustomUIElements/StyledSnackbar';
import Responses from './Responses';
import FormResponses from './FormResponses';

export default function ExternalView({
  jobId,
  applicationId,
  externalViewId,
  loginHash
}: {
  jobId: number;
  applicationId: number;
  externalViewId: string;
  loginHash: string;
}) {
  const [state, dispatch] = useReducer(ExternalViewReducer, InitialExternalViewState);
  const { data: externalViewData } = useQuery({
    queryKey: ['externalViewData'],
    queryFn: async () => {
      const { res } = await Api.getExternalViewData(
        jobId,
        applicationId,
        externalViewId,
        loginHash
      );
      return {
        ...res,
        id: externalViewId,
        login_hash: loginHash
      };
    }
  });

  const applicationLink = externalViewData?.application?.application_link;
  const summaryData = externalViewData?.application?.summary;
  const attachmentsData = externalViewData?.application?.attachments;
  const smartForms = externalViewData?.application?.smart_forms;

  return (
    <Stack
      sx={{
        rowGap: { xs: 2, sm: 3 },
        padding: { xs: 1, sm: 0 }
      }}
    >
      <ExternalViewHeader dispatch={dispatch} />
      {applicationLink && (
        <Button
          sx={{
            ...sharedClasses.genericButton,
            width: { xs: '100%', sm: 'fit-content' },
            fontSize: { xs: '14px', sm: '16px' }
          }}
          onClick={() => window.open(applicationLink, '_blank')}
        >
          View Application in :Recruit
        </Button>
      )}
      {summaryData && (
        <Stack sx={{ ...styles.sectionContainer, rowGap: 'unset' }}>
          <Stack
            sx={{
              fontSize: { xs: '15px', sm: '16px' },
              fontWeight: 600
            }}
          >
            Summary
          </Stack>
          <Box
            sx={styles.summary}
            dangerouslySetInnerHTML={{
              __html: dompurify.sanitize(summaryData)
            }}
          />
        </Stack>
      )}
      <Responses />
      {!!attachmentsData?.length && <Attachments state={state} dispatch={dispatch} />}
      {!!smartForms?.length && <FormResponses />}
      <StyledSnackbar
        state={state.snackbar.state}
        message={state.snackbar.message}
        setSnackbarState={(snackbarInput: ISnackbarInput) =>
          dispatch({ type: 'SET_SNACKBAR', payload: snackbarInput })
        }
      />
    </Stack>
  );
}
